import { EmailRounded } from "@mui/icons-material";
import { CircularProgress, FormGroup, Grid, Typography } from "@mui/material";
import { useUserInfo } from "@sans-service-tier/sdk-react";
import { detect } from "detect-browser";
import React, { useCallback, useContext, useState } from "react";

import { HasbotContext, TechSupportType } from "../../context/hasBot-context";
import { CourseFragment } from "../../hooks/__generated__/CourseFragment";
import { FullCourseFragment } from "../../hooks/__generated__/FullCourseFragment";
import { ModuleFragment } from "../../hooks/__generated__/ModuleFragment";
import { SlideFragment } from "../../hooks/__generated__/SlideFragment";
import { useAppState } from "../../hooks/useAppState";
import { CategoryType, useTechSupport } from "../../hooks/useTechSupport";
import { AutomatedMessage } from "./Common.elements";
import { HelpAndSupportHeader } from "./HelpAndSupportHeader";
import {
  MessageActionButtons,
  MessageActionsContainer,
  PageContainer,
  StyledCheckbox,
  StyledFormLabel,
  StyledLabel,
  SupportMessageTextField,
  Title,
} from "./TechSupportMessageDialog.elements";

// Type definitions for proper typing
interface UserInfo {
  email: string;
  given_name: string;
  family_name: string;
  "cognito:username": string;
}

interface DetectBrowserResult {
  name?: string;
  version?: string | null;
  os?: string | null;
}

const buildEmailPayload = (
  sendMessageCopy: boolean,
  messageText: string,
  shortDescription: string,
  userInfo: UserInfo,
  slideId: string | undefined,
  courseIdStr: string | undefined,
) => {
  if (!sendMessageCopy) {
    return undefined;
  }
  return {
    attributes: {
      slideUuid: slideId ?? "00000000-0000-0000-0000-000000000000",
      courseId: courseIdStr ?? "Unknown",
      token: "",
    },
    body: messageText,
    subject: shortDescription,
    to: userInfo.email,
  };
};

const buildMessageAttributes = (
  browser: DetectBrowserResult | null,
  courseId: number | null | undefined,
  course: CourseFragment | FullCourseFragment | null | undefined,
  slide: SlideFragment | null | undefined,
  module: ModuleFragment | null | undefined,
) => {
  return {
    agent: {
      app: {
        version: "OnDemand Web 2.0",
      },
      name: browser?.name ?? "Unknown",
      os: browser?.os ?? "Unknown",
      version: browser?.version ?? "Unknown",
    },
    course: {
      id: courseId ?? 0,
      name: course?.shortName ?? "Unknown",
      version: course?.version ?? "Unknown",
    },
    slide: {
      index: slide?.number ?? 0,
      name: slide?.name ?? "Quiz/Eval",
      uuid: slide?.id ?? "00000000-0000-0000-0000-000000000000",
    },
    module: {
      name: module?.name ?? "Unknown",
      number: module?.fullNumber ? +module?.fullNumber : 0,
      uuid: module?.id ?? "Unknown",
    },
  };
};

const buildUserPayload = (userInfo: UserInfo) => {
  return {
    email: userInfo.email,
    firstName: userInfo.given_name,
    id: userInfo["cognito:username"],
    lastName: userInfo.family_name,
  };
};

export function TechSupportMessageDialog(): React.ReactElement {
  const [messageText, setMessageText] = useState("");
  const [messageHasBeenSent, setMessageHasBeenSent] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [sendMessageCopy, setSendMessageCopy] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const techSupport = useTechSupport();
  const { techSupportActive } = useContext(HasbotContext);
  const browser = detect();
  const userInfo = useUserInfo() as UserInfo;
  const {
    course,
    module,
    slide,
    appState: { courseId },
  } = useAppState();

  const title = techSupportActive === TechSupportType.CSS ? "Email Support" : "Email SME";

  const handleCopyMeToggle = () => {
    setSendMessageCopy(!sendMessageCopy);
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleCopyMeToggle();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => setMessageText(event.target.value);

  const sendMessage = useCallback(async () => {
    setIsSending(true);
    setError(undefined);

    const category =
      techSupportActive === TechSupportType.CSS ? CategoryType.Technical_Support : CategoryType.Content_Questions;

    const shortDescription = "SANS OnDemand - " + userInfo.given_name + " " + userInfo.family_name + " " + course?.name;

    const emailPayload = buildEmailPayload(
      sendMessageCopy,
      messageText,
      shortDescription,
      userInfo,
      slide?.id,
      courseId?.toString(),
    );

    const attributesPayload = buildMessageAttributes(browser, courseId, course, slide, module);
    const userPayload = buildUserPayload(userInfo);

    const data = await techSupport.sendMessage({
      assignmentGroup: techSupportActive as TechSupportType,
      attributes: attributesPayload,
      category,
      description: messageText,
      email: emailPayload,
      message: messageText,
      shortDescription: shortDescription,
      user: userPayload,
    });

    if (data?.error) {
      setError(data.error);
      setIsSending(false);
      return;
    }

    setMessageText("");
    setMessageHasBeenSent(true);
    setIsSending(false);
  }, [
    techSupportActive,
    messageText,
    sendMessageCopy,
    techSupport,
    userInfo,
    course,
    slide,
    module,
    browser,
    courseId,
  ]);

  return (
    <Grid container flexDirection="column">
      <HelpAndSupportHeader>
        <Grid
          container
          size={{ xs: 6 }}
          gap={1}
          sx={{ flexGrow: 1 }}
          alignItems="center"
          aria-live="assertive"
          className="header-wrapper"
        >
          <EmailRounded
            sx={{ color: theme => theme.custom.palette.white, width: 20, height: 20 }}
            data-testid="EmailIcon"
          />
          <Title className="tech-support-header">{title}</Title>
        </Grid>
      </HelpAndSupportHeader>
      <PageContainer container direction="column" gap={2}>
        {messageHasBeenSent ? (
          <AutomatedMessage>Thank you for contacting us! We will get back to you as soon as possible.</AutomatedMessage>
        ) : (
          <>
            <AutomatedMessage>
              We’ll reply within 2 hours (business hours) or within 24 hours (outside business hours).
            </AutomatedMessage>

            <Grid container direction="column" gap={0.5}>
              <StyledLabel htmlFor="support-message">Message</StyledLabel>
              <SupportMessageTextField
                autoFocus
                id="support-message"
                placeholder="Please include any relevant details to help us solve the issue"
                value={messageText}
                onChange={handleChange}
                aria-label="support message"
              />
              {error && (
                <Typography marginY={2} color="red" fontSize={14}>
                  {error} Please try again.
                </Typography>
              )}
            </Grid>
            <MessageActionsContainer sx={{ margin: 0 }}>
              <FormGroup>
                <StyledFormLabel
                  control={
                    <StyledCheckbox
                      name="copyMe"
                      id="send-me-copy"
                      checked={sendMessageCopy}
                      onChange={handleCopyMeToggle}
                      onKeyUp={handleKeyUp}
                    />
                  }
                  label="Send me a copy"
                />
              </FormGroup>
            </MessageActionsContainer>

            <MessageActionButtons
              disabled={messageText.length <= 0 || isSending}
              variant="contained"
              startIcon={isSending ? <CircularProgress size={24} /> : null}
              onClick={sendMessage}
              id="send-support-message"
              fullWidth
              size="large"
            >
              Send
            </MessageActionButtons>
          </>
        )}
      </PageContainer>
    </Grid>
  );
}
